import {Component, inject, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {CommonTableComponent} from '@lib-ui/common-table/ui/common-table.component';
import {LoaderComponent} from '@lib-ui/loader/loader.component';
import {ICommonTableAction, ICommonTableConfig} from '@lib-ui/common-table/models/common-table.interface';
import {CommonTableConfig} from '@lib-ui/common-table/models/common-table-config.class';
import {MatDialog} from '@angular/material/dialog';
import {RowAction} from '@lib-ui/common-table/models/common-table.enum';
import {PriceListTableService} from './price-list-table.service';
import {PriceEditorComponent} from '../../components/price-editor/price-editor.component';
import {AdvancedSearchComponent} from '@lib-ui/advanced-search/ui/advanced-search.component';
import {FilterConfig} from '@lib-ui/advanced-search/models/advanced-search.interface';
import {FormControl} from '@angular/forms';
import {CategorySelectorService} from '@lib-services/category-selector.service';
import {filter, switchMap} from 'rxjs';
import {MessageService} from '@lib-services/message.service';
import {PriceService} from '@lib-services/price.service';
import { AuthService } from '@lib-ui/auth/auth.service';
import { ExternalPriceService } from '@lib-services/external-price.service';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-price-list',
    standalone: true,
    imports: [CommonModule, CommonTableComponent, LoaderComponent, AdvancedSearchComponent, MatButton],
    templateUrl: './price-list.component.html',
    styleUrl: './price-list.component.scss'
})
export class PriceListComponent implements OnInit {
    tableConfig: ICommonTableConfig = new CommonTableConfig( {
        columns: [
            { name: 'id', title: 'ИН'},
            { name: 'workName', title: 'Работа' },
            { name: 'categoryName', title: 'Категория' },
            { name: 'duration', title: 'Длительность, мин' },
            { name: 'price', title: 'Цена, руб' },
            { name: 'discountPrice', title: 'Цена со скидкой, руб' },
            { name: 'externalPriceName', title: 'Позиция в кассе', showToolTip: true, hidden: false },
        ],
        createButtonLabel: 'Добавить прайс',
        showCreateButton: true,
        withCheckboxes: true,
        rowActions: [RowAction.DELETE],
    });
    filterConfig: FilterConfig[] = [
        // {
        //     type: 'common-dropdown',
        //     label: 'Компания',
        //     name: 'category.company.id',
        //     commonDropdownConfig: {
        //         service: inject(CompanySelectorService),
        //         formCtrl: new FormControl(),
        //     },
        // },
        {
            type: 'common-dropdown',
            label: 'Категория',
            name: 'category.id',
            commonDropdownConfig: {
                service: inject(CategorySelectorService),
                formCtrl: new FormControl(),
            },
        },
        {
            type: 'input',
            label: 'Работа',
            name: 'work.name'
        },
    ];

    protected dialog = inject(MatDialog);
    protected priceTableService = inject(PriceListTableService);
    protected messageService = inject(MessageService);
    protected priceService = inject(PriceService);
    protected authService = inject(AuthService);
    protected externalPriceService = inject(ExternalPriceService);

    ngOnInit(): void {
        const externalPriceColumn = this.tableConfig.columns.find((column) => column.name === 'externalPriceName');
        if (externalPriceColumn) {
            externalPriceColumn.hidden = !this.canUsePaymentDevice;
        }
    }

    applyAction(value: ICommonTableAction): void {
        if (value.action === RowAction.CLICK) {
            this.openDialog({ priceId: Number(value.data[0]['id']) });
        }
        if (value.action === RowAction.CREATE) {
            this.openDialog({});
        }
        if (value.action === RowAction.DELETE) {
            const ids = value.data.map(el => Number(el['id']));
            this.deletePrices(ids);
        }
    }

    openDialog(data: { priceId?: number }): void  {
        this.dialog.open(PriceEditorComponent, {
            width: '50%',
            height: '50%',
            data: data,
            disableClose: true,
        }).afterClosed().subscribe({
            next: () => {
                this.priceTableService.updateData(this.tableConfig);
            }
        });
    };

    applyFilters(filterData: Record<string, string | null>): void {
        this.tableConfig.filters = filterData;
        this.tableConfig.paginatorConfig.pageIndex = 0;
        this.priceTableService.updateData(this.tableConfig);
    }

    deletePrices(ids: number[]): void {
        this.messageService.confirmMessage({
            title: 'Удаление прайса(ов)',
            text: 'Вы действительно хотите удалить выбранные прайсы?',
            primaryText: 'Удалить',
            secondaryText: 'Отмена',
            showSecondaryButton: true,
        }).pipe(
            filter(result => result),
            switchMap(() => {
                return this.priceService.deletePrices(ids);
            }),
        ).subscribe({
            next: () => {
                this.messageService.successMessage('Прайсы успешно удалены');
                this.priceTableService.updateData(this.tableConfig);
            },
            error: () => {
                this.messageService.errorMessage('При удалении произошла ошибка');
            }
        });
    }

    syncExternalPrices(): void {
        this.externalPriceService.syncAqsiPrices().subscribe({
            next: (result) => {
                this.messageService.successMessage(
                    `Загрузка завершена: добавлено ${result.created}, обновлено ${result.updated}, деактивировано ${result.inactivated}`,
                );
            },
            error: (error) => {
                this.messageService.errorMessage(error.error?.message || 'Ошибка загрузки прайсов из кассы');
            },
        });
    }

    get canUsePaymentDevice(): boolean {
        return !!this.authService.user().data?.company?.settings?.paymentDevice;
    }
}
